/**
 * Validation for lab test units to ensure they are appropriate for each test type
 */

// Valid units for each test type
export const validUnitsForTest: Record<string, string[]> = {
  // Lipid Panel
  'cholesterol': ['mg/dL', 'mmol/L'],
  'ldl': ['mg/dL', 'mmol/L'],
  'ldl cholesterol': ['mg/dL', 'mmol/L'],
  'ldl-c': ['mg/dL', 'mmol/L'],
  'hdl': ['mg/dL', 'mmol/L'],
  'hdl cholesterol': ['mg/dL', 'mmol/L'],
  'hdl-c': ['mg/dL', 'mmol/L'],
  'triglycerides': ['mg/dL', 'mmol/L'],
  
  // Blood glucose
  'glucose': ['mg/dL', 'mmol/L'],
  'blood glucose': ['mg/dL', 'mmol/L'],
  'hba1c': ['%', 'mmol/mol'],
  'hemoglobin a1c': ['%', 'mmol/mol'],
  
  // Kidney function
  'creatinine': ['mg/dL', 'μmol/L', 'umol/L'],
  'bun': ['mg/dL', 'mmol/L'],
  'urea': ['mg/dL', 'mmol/L'],
  'egfr': ['mL/min/1.73m²', 'mL/min/1.73m2'],
  
  // Liver function
  'alt': ['U/L', 'IU/L'],
  'ast': ['U/L', 'IU/L'],
  'alkaline phosphatase': ['U/L', 'IU/L'],
  'ggt': ['U/L', 'IU/L'],
  'bilirubin': ['mg/dL', 'μmol/L', 'umol/L'],
  
  // Complete Blood Count
  'hemoglobin': ['g/dL', 'g/L', 'mmol/L'],
  'hematocrit': ['%', 'L/L'],
  'rbc': ['M/uL', '10^6/uL', '10^12/L', 'T/L'],
  'wbc': ['K/uL', '10^3/uL', '10^9/L', 'G/L'],
  'platelets': ['K/uL', '10^3/uL', '10^9/L', 'G/L'],
  'mcv': ['fL'],
  'mch': ['pg'],
  'mchc': ['g/dL', 'g/L'],
  'rdw': ['%'],
  
  // Hormones
  'testosterone': ['ng/dL', 'nmol/L'],
  'tsh': ['mIU/L', 'μIU/mL', 'uIU/mL'],
  'free t4': ['ng/dL', 'pmol/L'],
  'free t3': ['pg/mL', 'pmol/L'],
  
  // Vitamins/Minerals
  'vitamin d': ['ng/mL', 'nmol/L'],
  '25-hydroxyvitamin d': ['ng/mL', 'nmol/L'],
  'vitamin b12': ['pg/mL', 'pmol/L'],
  'folate': ['ng/mL', 'nmol/L'],
  'ferritin': ['ng/mL', 'μg/L', 'ug/L'],
  'iron': ['μg/dL', 'ug/dL', 'μmol/L', 'umol/L'],
  
  // Prostate
  'psa': ['ng/mL', 'μg/L', 'ug/L']
};

/**
 * Check if a unit is valid for a given test
 */
export function isValidUnitForTest(testName: string, unit: string): boolean {
  const normalizedTest = testName.toLowerCase().trim();
  const normalizedUnit = unit.trim();
  
  // Find matching test
  for (const [test, validUnits] of Object.entries(validUnitsForTest)) {
    if (normalizedTest.includes(test) || test.includes(normalizedTest)) {
      return validUnits.some(validUnit => 
        validUnit.toLowerCase() === normalizedUnit.toLowerCase()
      );
    }
  }
  
  // If test not found, we can't validate
  return true; // Default to allowing it
}

/**
 * Get suggested units for a test when current unit is invalid
 */
export function getSuggestedUnits(testName: string, currentUnit: string): string[] {
  const normalizedTest = testName.toLowerCase().trim();
  
  // Find matching test
  for (const [test, validUnits] of Object.entries(validUnitsForTest)) {
    if (normalizedTest.includes(test) || test.includes(normalizedTest)) {
      return validUnits;
    }
  }
  
  return [];
}

/**
 * Detect if a unit is likely incorrect based on the value and test type
 */
export function detectLikelyUnitError(testName: string, value: number, unit: string): {
  isLikelyError: boolean;
  suggestedUnit?: string;
  reason?: string;
} {
  const normalizedTest = testName.toLowerCase().trim();
  const normalizedUnit = unit.toLowerCase().trim();
  
  // Check if U/L or U/l is used for tests that shouldn't have it
  if ((normalizedUnit === 'u/l' || normalizedUnit === 'u/l') && 
      (normalizedTest.includes('ldl') || 
       normalizedTest.includes('hdl') || 
       normalizedTest.includes('cholesterol') ||
       normalizedTest.includes('glucose') ||
       normalizedTest.includes('creatinine') ||
       normalizedTest.includes('hemoglobin'))) {
    
    // These tests should never use U/L
    const suggestedUnits = getSuggestedUnits(testName, unit);
    return {
      isLikelyError: true,
      suggestedUnit: suggestedUnits[0] || 'mg/dL',
      reason: `${testName} should not be measured in ${unit}. Common units are: ${suggestedUnits.join(', ')}`
    };
  }
  
  // Check for value-based inconsistencies
  if (normalizedTest.includes('ldl')) {
    // LDL in mg/dL typically ranges 50-200
    // LDL in mmol/L typically ranges 1.3-5.2
    if (normalizedUnit === 'mg/dl' && value < 10) {
      return {
        isLikelyError: true,
        suggestedUnit: 'mmol/L',
        reason: 'LDL value seems too low for mg/dL, might be mmol/L'
      };
    }
    if (normalizedUnit === 'mmol/l' && value > 50) {
      return {
        isLikelyError: true,
        suggestedUnit: 'mg/dL',
        reason: 'LDL value seems too high for mmol/L, might be mg/dL'
      };
    }
  }
  
  return { isLikelyError: false };
}